/**
 * ThemeService — modo día/noche
 *
 * Conmuta el tema claro/oscuro añadiendo o quitando la clase `.app-dark` en
 * `<html>` (el `darkModeSelector: '.app-dark'` está configurado en
 * app.config.ts → providePrimeNG). Los tokens oscuros de PrimeNG y los
 * `--ts-*` (styles.scss) reaccionan a esa clase.
 *
 * - `isDark` es un signal reactivo para usar en templates (p.ej. el toggle del
 *   header y conmutar el logo color/blanco).
 * - Persiste la preferencia en localStorage; si no hay ninguna guardada, usa
 *   `prefers-color-scheme` del sistema.
 * - SSR-safe: en el servidor no toca el DOM (queda claro por defecto) y el
 *   cliente aplica la preferencia real al hidratar desde init().
 */
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

const STORAGE_KEY = 'app_theme';
const DARK_CLASS = 'app-dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private platformId = inject(PLATFORM_ID);

  /** true = modo oscuro activo. */
  readonly isDark = signal<boolean>(false);

  /** Llamar una vez desde AppComponent.ngOnInit(). */
  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const stored = localStorage.getItem(STORAGE_KEY);
    const prefersDark =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches;

    const dark = stored ? stored === 'dark' : prefersDark;
    this.apply(dark);
  }

  /** Alterna entre claro y oscuro y persiste la preferencia. */
  toggle(): void {
    this.set(!this.isDark());
  }

  /** Fija el modo explícitamente. */
  set(dark: boolean): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.apply(dark);
    localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light');
  }

  private apply(dark: boolean): void {
    this.isDark.set(dark);
    document.documentElement.classList.toggle(DARK_CLASS, dark);
  }
}
